"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { useRef, type MouseEvent } from "react";
import { cn } from "@/lib/utils";
interface MegaMenuTriggerProps {
  label: string;
  /** The menu's hub: the label is a link to it. */
  href: string;
  isOpen: boolean;
  /** The page being viewed belongs to this menu: marked like an open menu, in bold. */
  isCurrent?: boolean;
  onOpen: () => void;
  onNavigate: () => void;
  /** Id of the panel this trigger opens: the shared shelf, or a dropdown's own panel. */
  controlsId: string;
}

/**
 * The menu's label, a link to its hub. With a mouse, hovering opens the menu
 * and a click goes to the hub. Without hover (touch, keyboard) the first
 * click opens the menu and a second one goes to the hub, so the menu stays
 * reachable on an iPad and from the keyboard.
 *
 * Fills the header bar's height, so the open menu's underline sits on the
 * bar's lower edge. A 3px top padding balances that 3px border, so the label
 * stays centred on the bar, on the baseline the header's text shares
 * (`e2e/components/tabs.spec.ts`). An invisible bold copy of the label (the
 * `::after`) holds the label at its bold width: the current menu's label drops
 * its bold while another menu is open, and without it every label after it
 * shifted.
 */
export function MegaMenuTrigger(t0) {
  const $ = _c(17);
  const {
    label,
    href,
    isOpen,
    isCurrent: t1,
    onOpen,
    onNavigate,
    controlsId
  } = t0;
  const isCurrent = t1 === undefined ? false : t1;
  const press = useRef(null);
  let t2;
  if ($[0] !== isOpen || $[1] !== onNavigate || $[2] !== onOpen) {
    t2 = event => {
      const pressed = event.detail > 0 ? press.current : null;
      press.current = null;
      if (pressed?.mouse || (pressed ? pressed.wasOpen : isOpen)) {
        onNavigate();
        return;
      }
      event.preventDefault();
      onOpen();
    };
    $[0] = isOpen;
    $[1] = onNavigate;
    $[2] = onOpen;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClick = t2;
  let t3;
  if ($[4] !== isOpen) {
    t3 = event_0 => {
      press.current = {
        mouse: event_0.pointerType === "mouse",
        wasOpen: isOpen
      };
    };
    $[4] = isOpen;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = isCurrent ? "font-bold" : "font-semibold";
  const t5 = isOpen || isCurrent ? "text-primary border-accent" : "border-transparent text-on-surface-variant hover:text-primary";
  let t6;
  if ($[6] !== t4 || $[7] !== t5) {
    t6 = cn("flex h-full flex-col items-center justify-center px-3.5 pt-[3px] text-[15px] whitespace-nowrap border-b-[3px] transition-colors duration-200", "after:invisible after:h-0 after:overflow-hidden after:font-bold after:content-[attr(data-label)_/_'']", t4, t5);
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== controlsId || $[10] !== handleClick || $[11] !== href || $[12] !== isOpen || $[13] !== label || $[14] !== t3 || $[15] !== t6) {
    t7 = <Link href={href} onPointerDown={t3} onClick={handleClick} data-label={label} className={t6} aria-expanded={isOpen} aria-haspopup="true" aria-controls={controlsId}>{label}</Link>;
    $[9] = controlsId;
    $[10] = handleClick;
    $[11] = href;
    $[12] = isOpen;
    $[13] = label;
    $[14] = t3;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  return t7;
}